import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Tabs,
  Tab,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
  Alert,
  InputAdornment,
  Link,
  Switch,
  FormControlLabel,
  Fade,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArchiveIcon from "@mui/icons-material/Archive";
import SyncIcon from "@mui/icons-material/Sync";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ShoppingList, ShoppingListItem } from "../../lib/types";
import { getRemainingShoppingListTotal } from "../../lib/expenseUtils";
import { shoppingListAPI } from "../../services/api/api";
import PageShareModal from "../common/PageShareModal";
import { useShareAction } from "../../contexts/ShareActionContext";

// Inline-editable item row: each field saves on blur when changed
const EditableItemRow: React.FC<{
  item: ShoppingListItem;
  hideDueDate?: boolean;
  isPurchasing?: boolean;
  isAnimatingOut?: boolean;
  onUpdate: (itemId: string, updates: Record<string, any>) => Promise<void>;
  onPurchased: (item: ShoppingListItem) => Promise<void>;
  onDelete: (itemId: string) => void;
}> = ({
  item,
  hideDueDate = false,
  isPurchasing = false,
  isAnimatingOut = false,
  onUpdate,
  onPurchased,
  onDelete,
}) => {
  const [title, setTitle] = useState(item.title);
  const [vendor, setVendor] = useState(item.vendor || "");
  const [link, setLink] = useState(item.link || "");
  const [price, setPrice] = useState(String(item.price));
  const [dueDate, setDueDate] = useState(
    item.dueDate ? new Date(item.dueDate).toISOString().split("T")[0] : ""
  );

  // Keep local state in sync with prop changes (e.g. after API returns updated data)
  const prevItemRef = useRef(item);
  useEffect(() => {
    if (prevItemRef.current.id !== item.id || prevItemRef.current !== item) {
      setTitle(item.title);
      setVendor(item.vendor || "");
      setLink(item.link || "");
      setPrice(String(item.price));
      setDueDate(
        item.dueDate ? new Date(item.dueDate).toISOString().split("T")[0] : ""
      );
      prevItemRef.current = item;
    }
  }, [item]);

  const saveIfChanged = useCallback(
    (field: string, localValue: string) => {
      let current: string;
      let payload: Record<string, any>;

      switch (field) {
        case "title":
          current = item.title;
          if (localValue.trim() && localValue.trim() !== current) {
            payload = { title: localValue.trim() };
          } else if (!localValue.trim()) {
            setTitle(item.title); // revert empty
            return;
          } else return;
          break;
        case "vendor":
          current = item.vendor || "";
          if (localValue.trim() !== current) {
            payload = { vendor: localValue.trim() || undefined };
          } else return;
          break;
        case "link":
          current = item.link || "";
          if (localValue.trim() !== current) {
            payload = { link: localValue.trim() || undefined };
          } else return;
          break;
        case "price": {
          const numVal = parseFloat(localValue) || 0;
          if (numVal !== item.price) {
            payload = { price: numVal };
          } else return;
          break;
        }
        case "dueDate":
          current = item.dueDate
            ? new Date(item.dueDate).toISOString().split("T")[0]
            : "";
          if (localValue !== current) {
            payload = { dueDate: localValue || undefined };
          } else return;
          break;
        default:
          return;
      }

      console.debug(
        `[EditableItemRow] Saving ${field} for item ${item.id}:`,
        payload
      );
      onUpdate(item.id, payload);
    },
    [item, onUpdate]
  );

  return (
    <Fade in={!isAnimatingOut} timeout={220}>
      <TableRow
        hover
        sx={{
          opacity: isAnimatingOut ? 0 : 1,
          transform: isAnimatingOut ? "translateY(-10px)" : "translateY(0)",
          transition: "opacity 220ms ease, transform 220ms ease",
        }}
      >
        <TableCell sx={{ py: 0.5 }}>
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => saveIfChanged("title", title)}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            sx={{ "& input": { py: 0.5 } }}
          />
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          <TextField
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            onBlur={() => saveIfChanged("vendor", vendor)}
            size="small"
            fullWidth
            variant="standard"
            InputProps={{ disableUnderline: true }}
            placeholder="-"
            sx={{ "& input": { py: 0.5 } }}
          />
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <TextField
              value={link}
              onChange={(e) => setLink(e.target.value)}
              onBlur={() => saveIfChanged("link", link)}
              size="small"
              fullWidth
              variant="standard"
              InputProps={{ disableUnderline: true }}
              placeholder="-"
              sx={{ "& input": { py: 0.5 } }}
            />
            {link && (
              <Link
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ flexShrink: 0 }}
              >
                <OpenInNewIcon fontSize="small" />
              </Link>
            )}
          </Box>
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <TextField
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            onBlur={() => saveIfChanged("price", price)}
            size="small"
            type="number"
            variant="standard"
            InputProps={{
              disableUnderline: true,
              endAdornment: <InputAdornment position="end">$</InputAdornment>,
            }}
            sx={{ width: 100, "& input": { py: 0.5, textAlign: "right" } }}
          />
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {item.dateRequested
              ? new Date(item.dateRequested).toLocaleDateString("en-US", {
                  timeZone: "UTC",
                })
              : "-"}
          </Typography>
        </TableCell>
        <TableCell sx={{ py: 0.5 }}>
          {hideDueDate ? (
            <Typography variant="body2" color="text.secondary">
              Recurring list
            </Typography>
          ) : (
            <TextField
              value={dueDate}
              onChange={(e) => {
                setDueDate(e.target.value);
                const current = item.dueDate
                  ? new Date(item.dueDate).toISOString().split("T")[0]
                  : "";
                if (e.target.value !== current) {
                  console.debug(
                    `[EditableItemRow] Saving dueDate for item ${item.id}`
                  );
                  onUpdate(item.id, { dueDate: e.target.value || undefined });
                }
              }}
              size="small"
              type="date"
              variant="standard"
              InputProps={{ disableUnderline: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ "& input": { py: 0.5 } }}
            />
          )}
        </TableCell>
        <TableCell align="right" sx={{ py: 0.5 }}>
          <Box
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={0.5}
          >
            {!item.isPaid && (
              <Tooltip title="Purchased">
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    aria-label={`Purchased ${item.title}`}
                    disabled={isPurchasing}
                    onClick={() => void onPurchased(item)}
                  >
                    {isPurchasing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <CheckCircleIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton
              size="small"
              color="error"
              aria-label={`Delete ${item.title}`}
              disabled={isPurchasing}
              onClick={() => onDelete(item.id)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    </Fade>
  );
};

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const WEEK_OF_MONTH_OPTIONS = [
  { value: 1, label: "1st week" },
  { value: 2, label: "2nd week" },
  { value: 3, label: "3rd week" },
  { value: 4, label: "4th week" },
  { value: 5, label: "5th week (when applicable)" },
];

const MONTH_OPTIONS = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const ANNUAL_MODE_OPTIONS = [
  { value: "every_month", label: "Every Month" },
  { value: "every_other_month", label: "Every Other Month" },
  { value: "quarterly", label: "Every Quarter" },
  { value: "every_6_months", label: "Every 6 Months" },
  { value: "specific_months", label: "Specific Months" },
];

const ShoppingGroceries: React.FC = () => {
  const { setShareHandler } = useShareAction();
  const [lists, setLists] = useState<ShoppingList[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0); // 0=active, 1=archived
  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  // New list dialog
  const [newListOpen, setNewListOpen] = useState(false);
  const [newListTitle, setNewListTitle] = useState("");
  const [newListIsRecurring, setNewListIsRecurring] = useState(false);
  const [newListRecurrenceType, setNewListRecurrenceType] = useState<
    "daily" | "weekly" | "monthly" | "annually"
  >("weekly");
  const [newListWeekdays, setNewListWeekdays] = useState<number[]>([
    new Date().getDay(),
  ]);
  const [newListWeekday, setNewListWeekday] = useState<number>(
    new Date().getDay()
  );
  const [newListWeekOfMonth, setNewListWeekOfMonth] = useState<
    1 | 2 | 3 | 4 | 5
  >(1);
  const [newListAnnualMode, setNewListAnnualMode] = useState<
    | "every_month"
    | "every_other_month"
    | "quarterly"
    | "every_6_months"
    | "specific_months"
  >("every_month");
  const [newListSpecificMonths, setNewListSpecificMonths] = useState<number[]>([
    new Date().getMonth() + 1,
  ]);

  // Edit list title
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState("");

  // Page share
  const [pageShareOpen, setPageShareOpen] = useState(false);

  // New item form
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemVendor, setNewItemVendor] = useState("");
  const [newItemLink, setNewItemLink] = useState("");
  const [newItemPrice, setNewItemPrice] = useState("");
  const [newItemDueDate, setNewItemDueDate] = useState("");
  const [showPurchasedItems, setShowPurchasedItems] = useState(false);
  const [itemActionLoadingIds, setItemActionLoadingIds] = useState<Set<string>>(
    new Set()
  );
  const [purchasedAnimatingOutIds, setPurchasedAnimatingOutIds] = useState<
    Set<string>
  >(new Set());
  const [purchasedHiddenIds, setPurchasedHiddenIds] = useState<Set<string>>(
    new Set()
  );
  const purchasedTimeoutsRef = useRef<Record<string, number>>({});

  const fetchLists = useCallback(async () => {
    try {
      setLoading(true);
      const data = await shoppingListAPI.getAll({
        includeShared: true,
      });
      console.debug("[ShoppingGroceries] Fetched lists:", data.length);
      setLists(data);
      setError(null);
    } catch (err: any) {
      console.error("[ShoppingGroceries] Fetch error:", err);
      setError(err.message || "Failed to load shopping lists");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  useEffect(() => {
    setShareHandler(() => {
      setPageShareOpen(true);
    });
    return () => setShareHandler(null);
  }, [setShareHandler]);

  useEffect(
    () => () => {
      Object.values(purchasedTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      purchasedTimeoutsRef.current = {};
    },
    []
  );

  const activeLists = lists.filter((l) => l.status === "active");
  const archivedLists = lists.filter((l) => l.status === "archived");
  const displayedLists = tabIndex === 0 ? activeLists : archivedLists;
  const selectedList = lists.find((l) => l.id === selectedListId) || null;

  const getTotal = (list: ShoppingList) =>
    getRemainingShoppingListTotal(list.items);

  const getVisibleItems = useCallback(
    (list: ShoppingList | null) => {
      if (!list) return [];
      return list.items
        .filter((item) => !purchasedHiddenIds.has(item.id))
        .filter((item) =>
          showPurchasedItems
            ? true
            : !item.isPaid || purchasedAnimatingOutIds.has(item.id)
        );
    },
    [purchasedAnimatingOutIds, purchasedHiddenIds, showPurchasedItems]
  );
  const visibleSelectedItems = getVisibleItems(selectedList);

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);

  const buildRecurrenceConfig = useCallback(
    (type: "daily" | "weekly" | "monthly" | "annually") => {
      if (type === "weekly") {
        return { weekdays: newListWeekdays };
      }
      if (type === "monthly") {
        return { weekday: newListWeekday, weekOfMonth: newListWeekOfMonth };
      }
      if (type === "annually") {
        return {
          weekday: newListWeekday,
          weekOfMonth: newListWeekOfMonth,
          annualMode: newListAnnualMode,
          months:
            newListAnnualMode === "specific_months"
              ? newListSpecificMonths
              : undefined,
        };
      }
      return {};
    },
    [
      newListAnnualMode,
      newListSpecificMonths,
      newListWeekOfMonth,
      newListWeekday,
      newListWeekdays,
    ]
  );

  const recurrenceSummary = useCallback((list: ShoppingList) => {
    if (!list.isRecurring) return "One-time";
    const type = list.recurrenceType || "weekly";
    const config = list.recurrenceConfig || {};
    if (type === "daily") return "Daily";
    if (type === "weekly") {
      const labels = (config.weekdays || [])
        .map((d: number) => WEEKDAY_OPTIONS.find((o) => o.value === d)?.label)
        .filter(Boolean);
      return `Weekly (${labels.join(", ") || "Any day"})`;
    }
    if (type === "monthly") {
      const week = config.weekOfMonth || 1;
      const weekday = WEEKDAY_OPTIONS.find(
        (o) => o.value === Number(config.weekday ?? 0)
      )?.label;
      return `Monthly (${week}${week === 1 ? "st" : week === 2 ? "nd" : week === 3 ? "rd" : "th"} ${weekday || ""})`;
    }
    const annualModeLabel =
      ANNUAL_MODE_OPTIONS.find((o) => o.value === config.annualMode)?.label ||
      "Every Month";
    return `Annually (${annualModeLabel})`;
  }, []);

  const handleCreateList = async () => {
    if (!newListTitle.trim()) return;
    try {
      const created = await shoppingListAPI.create({
        title: newListTitle.trim(),
        isRecurring: newListIsRecurring,
        recurrenceType: newListRecurrenceType,
        recurrenceConfig: buildRecurrenceConfig(newListRecurrenceType),
      });
      console.debug("[ShoppingGroceries] Created list:", created.id);
      setLists((prev) => [created, ...prev]);
      setSelectedListId(created.id);
      setNewListOpen(false);
      setNewListTitle("");
      setNewListIsRecurring(false);
      setNewListRecurrenceType("weekly");
      setNewListWeekdays([new Date().getDay()]);
      setNewListWeekday(new Date().getDay());
      setNewListWeekOfMonth(1);
      setNewListAnnualMode("every_month");
      setNewListSpecificMonths([new Date().getMonth() + 1]);
    } catch (err: any) {
      console.error("[ShoppingGroceries] Create error:", err);
      setError(err.message);
    }
  };

  const handleUpdateTitle = async () => {
    if (!selectedList || !editTitleValue.trim()) return;
    try {
      const updated = await shoppingListAPI.update(selectedList.id, {
        title: editTitleValue.trim(),
      });
      setLists((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
      setEditingTitle(false);
    } catch (err: any) {
      console.error("[ShoppingGroceries] Update title error:", err);
      setError(err.message);
    }
  };

  const handleUpdateRecurring = async (enabled: boolean) => {
    if (!selectedList) return;
    try {
      const updated = await shoppingListAPI.update(selectedList.id, {
        isRecurring: enabled,
      });
      setLists((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    } catch (err: any) {
      console.error("[ShoppingGroceries] Update recurring error:", err);
      setError(err.message);
    }
  };

  const handleUpdateRecurrencePattern = async (pattern: string) => {
    if (!selectedList) return;
    try {
      const updated = await shoppingListAPI.update(selectedList.id, {
        recurrenceType: pattern as "daily" | "weekly" | "monthly" | "annually",
      });
      setLists((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    } catch (err: any) {
      console.error(
        "[ShoppingGroceries] Update recurrence pattern error:",
        err
      );
      setError(err.message);
    }
  };

  const handleUpdateRecurrenceConfig = async (config: Record<string, any>) => {
    if (!selectedList) return;
    try {
      const updated = await shoppingListAPI.update(selectedList.id, {
        recurrenceConfig: {
          ...(selectedList.recurrenceConfig || {}),
          ...config,
        },
      });
      setLists((prev) => prev.map((l) => (l.id === updated.id ? updated : l)));
    } catch (err: any) {
      console.error("[ShoppingGroceries] Update recurrence config error:", err);
      setError(err.message);
    }
  };

  const handleDeleteList = async (id: string) => {
    try {
      await shoppingListAPI.delete(id);
      setLists((prev) => prev.filter((l) => l.id !== id));
      if (selectedListId === id) setSelectedListId(null);
    } catch (err: any) {
      console.error("[ShoppingGroceries] Delete error:", err);
      setError(err.message);
    }
  };

  const handleArchiveList = async () => {
    if (!selectedList) return;
    try {
      const archived = await shoppingListAPI.archive(selectedList.id);
      setLists((prev) =>
        prev.map((l) => (l.id === archived.id ? archived : l))
      );
      setSelectedListId(null);
    } catch (err: any) {
      console.error("[ShoppingGroceries] Archive error:", err);
      setError(err.message);
    }
  };

  const handleSyncExpense = async () => {
    if (!selectedList) return;
    try {
      setSyncing(true);
      const result = await shoppingListAPI.syncExpense(selectedList.id);
      console.debug("[ShoppingGroceries] Synced expense:", result);
      // Refresh to get updated linked_expense_id
      await fetchLists();
    } catch (err: any) {
      console.error("[ShoppingGroceries] Sync error:", err);
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleAddItem = async () => {
    if (!selectedList || !newItemTitle.trim()) return;
    try {
      const item = await shoppingListAPI.addItem(selectedList.id, {
        title: newItemTitle.trim(),
        vendor: newItemVendor.trim() || undefined,
        link: newItemLink.trim() || undefined,
        price: parseFloat(newItemPrice) || 0,
        dueDate: selectedList.isRecurring
          ? undefined
          : newItemDueDate || undefined,
      });
      console.debug("[ShoppingGroceries] Added item:", item.id);
      setLists((prev) =>
        prev.map((l) =>
          l.id === selectedList.id ? { ...l, items: [...l.items, item] } : l
        )
      );
      setNewItemTitle("");
      setNewItemVendor("");
      setNewItemLink("");
      setNewItemPrice("");
      setNewItemDueDate("");
    } catch (err: any) {
      console.error("[ShoppingGroceries] Add item error:", err);
      setError(err.message);
    }
  };

  const handleUpdateItem = useCallback(
    async (itemId: string, updates: Record<string, any>) => {
      try {
        const updated = await shoppingListAPI.updateItem(itemId, updates);
        setLists((prev) =>
          prev.map((l) => ({
            ...l,
            items: l.items.map((i) => (i.id === updated.id ? updated : i)),
          }))
        );
      } catch (err: any) {
        console.error("[ShoppingGroceries] Update item error:", err);
        setError(err.message);
      }
    },
    []
  );

  const handleMarkItemPurchased = useCallback(
    async (item: ShoppingListItem) => {
      if (itemActionLoadingIds.has(item.id)) return;

      setItemActionLoadingIds((prev) => new Set(prev).add(item.id));
      try {
        const purchasedAt = new Date().toISOString();
        const updated = await shoppingListAPI.updateItem(item.id, {
          isPaid: true,
          paymentDate: purchasedAt,
          paymentAccount: "Purchased",
        });

        setLists((prev) =>
          prev.map((list) => ({
            ...list,
            items: list.items.map((existingItem) =>
              existingItem.id === updated.id ? updated : existingItem
            ),
          }))
        );

        console.debug("[ShoppingGroceries] Purchased item saved:", {
          itemId: item.id,
          paymentDate: purchasedAt,
        });

        setPurchasedAnimatingOutIds((prev) => new Set(prev).add(item.id));
        if (purchasedTimeoutsRef.current[item.id]) {
          window.clearTimeout(purchasedTimeoutsRef.current[item.id]);
        }
        purchasedTimeoutsRef.current[item.id] = window.setTimeout(() => {
          setPurchasedHiddenIds((prev) => new Set(prev).add(item.id));
          setPurchasedAnimatingOutIds((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          setItemActionLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          delete purchasedTimeoutsRef.current[item.id];
        }, 220);
      } catch (err: any) {
        console.error("[ShoppingGroceries] Purchase item error:", err);
        setError(err.message);
        setItemActionLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      }
    },
    [itemActionLoadingIds]
  );

  const handleDeleteItem = async (itemId: string) => {
    try {
      await shoppingListAPI.deleteItem(itemId);
      if (purchasedTimeoutsRef.current[itemId]) {
        window.clearTimeout(purchasedTimeoutsRef.current[itemId]);
        delete purchasedTimeoutsRef.current[itemId];
      }
      setItemActionLoadingIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      setPurchasedAnimatingOutIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      setPurchasedHiddenIds((prev) => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      setLists((prev) =>
        prev.map((l) => ({
          ...l,
          items: l.items.filter((i) => i.id !== itemId),
        }))
      );
    } catch (err: any) {
      console.error("[ShoppingGroceries] Delete item error:", err);
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <ShoppingCartIcon color="primary" />
          <Typography variant="h5">Lists</Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" gap={3} sx={{ minHeight: 500 }}>
        {/* Left Panel - List View */}
        <Paper sx={{ width: 320, flexShrink: 0, p: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              variant="fullWidth"
            >
              <Tab label={`Active (${activeLists.length})`} />
              <Tab label={`Archived (${archivedLists.length})`} />
            </Tabs>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            fullWidth
            sx={{ mb: 2 }}
            onClick={() => setNewListOpen(true)}
          >
            New List
          </Button>

          {displayedLists.length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              py={4}
            >
              No {tabIndex === 0 ? "active" : "archived"} lists
            </Typography>
          )}

          {displayedLists.map((list) => (
            <Paper
              key={list.id}
              variant="outlined"
              sx={{
                p: 1.5,
                mb: 1,
                cursor: "pointer",
                bgcolor:
                  selectedListId === list.id
                    ? "action.selected"
                    : "transparent",
                "&:hover": { bgcolor: "action.hover" },
              }}
              onClick={() => setSelectedListId(list.id)}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Box>
                  <Typography variant="subtitle2">{list.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {list.items.length} item{list.items.length !== 1 ? "s" : ""}{" "}
                    &middot; {formatCurrency(getTotal(list))}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.5}>
                  <Chip
                    label={recurrenceSummary(list)}
                    size="small"
                    variant="outlined"
                    color={list.isRecurring ? "secondary" : "default"}
                  />
                </Box>
              </Box>
              {list.linkedExpenseId && (
                <Chip
                  label="Synced"
                  size="small"
                  color="success"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Paper>
          ))}
        </Paper>

        {/* Right Panel - Detail View */}
        <Paper sx={{ flex: 1, p: 3 }}>
          {!selectedList ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              height="100%"
            >
              <ShoppingCartIcon
                sx={{ fontSize: 64, color: "text.disabled", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                Select a list or create a new one
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Title */}
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                {editingTitle ? (
                  <>
                    <TextField
                      value={editTitleValue}
                      onChange={(e) => setEditTitleValue(e.target.value)}
                      size="small"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleUpdateTitle();
                        if (e.key === "Escape") setEditingTitle(false);
                      }}
                    />
                    <Button size="small" onClick={handleUpdateTitle}>
                      Save
                    </Button>
                    <Button size="small" onClick={() => setEditingTitle(false)}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="h6">{selectedList.title}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setEditTitleValue(selectedList.title);
                        setEditingTitle(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Box>

              {/* Controls Row */}
              <Box
                display="flex"
                gap={2}
                mb={3}
                flexWrap="wrap"
                alignItems="center"
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(selectedList.isRecurring)}
                      onChange={(e) => handleUpdateRecurring(e.target.checked)}
                    />
                  }
                  label="Recurring"
                />

                {selectedList.isRecurring && (
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={selectedList.recurrenceType || "weekly"}
                      label="Frequency"
                      onChange={(e) =>
                        handleUpdateRecurrencePattern(e.target.value)
                      }
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="annually">Annually</MenuItem>
                    </Select>
                  </FormControl>
                )}

                {selectedList.isRecurring &&
                  (selectedList.recurrenceType || "weekly") === "weekly" && (
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <InputLabel>Days</InputLabel>
                      <Select
                        multiple
                        value={selectedList.recurrenceConfig?.weekdays || []}
                        label="Days"
                        onChange={(e) =>
                          handleUpdateRecurrenceConfig({
                            weekdays: (e.target.value as number[]).map(Number),
                          })
                        }
                      >
                        {WEEKDAY_OPTIONS.map((day) => (
                          <MenuItem key={day.value} value={day.value}>
                            {day.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                {selectedList.isRecurring &&
                  (selectedList.recurrenceType || "weekly") === "monthly" && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel>Week</InputLabel>
                        <Select
                          value={
                            selectedList.recurrenceConfig?.weekOfMonth || 1
                          }
                          label="Week"
                          onChange={(e) =>
                            handleUpdateRecurrenceConfig({
                              weekOfMonth: Number(e.target.value),
                            })
                          }
                        >
                          {WEEK_OF_MONTH_OPTIONS.map((week) => (
                            <MenuItem key={week.value} value={week.value}>
                              {week.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Day</InputLabel>
                        <Select
                          value={selectedList.recurrenceConfig?.weekday ?? 0}
                          label="Day"
                          onChange={(e) =>
                            handleUpdateRecurrenceConfig({
                              weekday: Number(e.target.value),
                            })
                          }
                        >
                          {WEEKDAY_OPTIONS.map((day) => (
                            <MenuItem key={day.value} value={day.value}>
                              {day.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </>
                  )}

                {selectedList.isRecurring &&
                  (selectedList.recurrenceType || "weekly") === "annually" && (
                    <>
                      <FormControl size="small" sx={{ minWidth: 220 }}>
                        <InputLabel>Annual Cadence</InputLabel>
                        <Select
                          value={
                            selectedList.recurrenceConfig?.annualMode ||
                            "every_month"
                          }
                          label="Annual Cadence"
                          onChange={(e) =>
                            handleUpdateRecurrenceConfig({
                              annualMode: e.target.value,
                            })
                          }
                        >
                          {ANNUAL_MODE_OPTIONS.map((mode) => (
                            <MenuItem key={mode.value} value={mode.value}>
                              {mode.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel>Week</InputLabel>
                        <Select
                          value={
                            selectedList.recurrenceConfig?.weekOfMonth || 1
                          }
                          label="Week"
                          onChange={(e) =>
                            handleUpdateRecurrenceConfig({
                              weekOfMonth: Number(e.target.value),
                            })
                          }
                        >
                          {WEEK_OF_MONTH_OPTIONS.map((week) => (
                            <MenuItem key={week.value} value={week.value}>
                              {week.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Day</InputLabel>
                        <Select
                          value={selectedList.recurrenceConfig?.weekday ?? 0}
                          label="Day"
                          onChange={(e) =>
                            handleUpdateRecurrenceConfig({
                              weekday: Number(e.target.value),
                            })
                          }
                        >
                          {WEEKDAY_OPTIONS.map((day) => (
                            <MenuItem key={day.value} value={day.value}>
                              {day.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {(selectedList.recurrenceConfig?.annualMode ||
                        "every_month") === "specific_months" && (
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                          <InputLabel>Months</InputLabel>
                          <Select
                            multiple
                            value={selectedList.recurrenceConfig?.months || []}
                            label="Months"
                            onChange={(e) =>
                              handleUpdateRecurrenceConfig({
                                months: (e.target.value as number[]).map(
                                  Number
                                ),
                              })
                            }
                          >
                            {MONTH_OPTIONS.map((month) => (
                              <MenuItem key={month.value} value={month.value}>
                                {month.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </>
                  )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={showPurchasedItems}
                      onChange={(e) => setShowPurchasedItems(e.target.checked)}
                    />
                  }
                  label="Show Purchased"
                />

                <Box flex={1} />

                <Tooltip title="Sync total as expense">
                  <Button
                    variant="outlined"
                    startIcon={
                      syncing ? <CircularProgress size={16} /> : <SyncIcon />
                    }
                    onClick={handleSyncExpense}
                    disabled={syncing}
                  >
                    {selectedList.linkedExpenseId
                      ? "Re-sync Expense"
                      : "Sync to Expenses"}
                  </Button>
                </Tooltip>

                {selectedList.status === "active" && (
                  <Tooltip title="Archive this list">
                    <IconButton onClick={handleArchiveList}>
                      <ArchiveIcon />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Delete this list">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteList(selectedList.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>

              {/* Total */}
              <Box mb={2}>
                <Typography variant="subtitle1">
                  Total:{" "}
                  <strong>{formatCurrency(getTotal(selectedList))}</strong>
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    ml={1}
                  >
                    ({visibleSelectedItems.length} visible item
                    {visibleSelectedItems.length !== 1 ? "s" : ""})
                  </Typography>
                </Typography>
              </Box>

              {/* Items Table */}
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "primary.main" }}>
                    <TableRow>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                      >
                        Title
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                      >
                        Vendor
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                      >
                        Link
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                        align="right"
                      >
                        Price
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                      >
                        Requested
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                      >
                        Due Date
                      </TableCell>
                      <TableCell
                        sx={{ color: "primary.contrastText", fontWeight: 600 }}
                        align="right"
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody sx={{ bgcolor: "background.default" }}>
                    {visibleSelectedItems.map((item) => (
                      <EditableItemRow
                        key={item.id}
                        item={item}
                        hideDueDate={Boolean(selectedList.isRecurring)}
                        isPurchasing={itemActionLoadingIds.has(item.id)}
                        isAnimatingOut={purchasedAnimatingOutIds.has(item.id)}
                        onUpdate={handleUpdateItem}
                        onPurchased={handleMarkItemPurchased}
                        onDelete={handleDeleteItem}
                      />
                    ))}

                    {/* Add Item Row */}
                    <TableRow>
                      <TableCell>
                        <TextField
                          value={newItemTitle}
                          onChange={(e) => setNewItemTitle(e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="Item name"
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleAddItem();
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={newItemVendor}
                          onChange={(e) => setNewItemVendor(e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="Vendor"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={newItemLink}
                          onChange={(e) => setNewItemLink(e.target.value)}
                          size="small"
                          fullWidth
                          placeholder="https://..."
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={newItemPrice}
                          onChange={(e) => setNewItemPrice(e.target.value)}
                          size="small"
                          type="number"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">$</InputAdornment>
                            ),
                          }}
                          sx={{ width: 100, "& input": { textAlign: "right" } }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          Today
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {selectedList.isRecurring ? (
                          <Typography variant="body2" color="text.secondary">
                            Recurring list
                          </Typography>
                        ) : (
                          <TextField
                            value={newItemDueDate}
                            onChange={(e) => setNewItemDueDate(e.target.value)}
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAddItem}
                          disabled={!newItemTitle.trim()}
                        >
                          Add
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      </Box>

      {/* New List Dialog */}
      <Dialog
        open={newListOpen}
        onClose={() => setNewListOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Shopping List</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="List Title"
              value={newListTitle}
              onChange={(e) => setNewListTitle(e.target.value)}
              fullWidth
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCreateList();
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newListIsRecurring}
                  onChange={(e) => setNewListIsRecurring(e.target.checked)}
                />
              }
              label="Recurring list"
            />
            {newListIsRecurring && (
              <>
                <FormControl fullWidth>
                  <InputLabel>Frequency</InputLabel>
                  <Select
                    value={newListRecurrenceType}
                    label="Frequency"
                    onChange={(e) =>
                      setNewListRecurrenceType(
                        e.target.value as
                          | "daily"
                          | "weekly"
                          | "monthly"
                          | "annually"
                      )
                    }
                  >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="annually">Annually</MenuItem>
                  </Select>
                </FormControl>

                {newListRecurrenceType === "weekly" && (
                  <FormControl fullWidth>
                    <InputLabel>Days</InputLabel>
                    <Select
                      multiple
                      value={newListWeekdays}
                      label="Days"
                      onChange={(e) =>
                        setNewListWeekdays(
                          (e.target.value as number[]).map(Number)
                        )
                      }
                    >
                      {WEEKDAY_OPTIONS.map((day) => (
                        <MenuItem key={day.value} value={day.value}>
                          {day.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {(newListRecurrenceType === "monthly" ||
                  newListRecurrenceType === "annually") && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Week Of Month</InputLabel>
                      <Select
                        value={newListWeekOfMonth}
                        label="Week Of Month"
                        onChange={(e) =>
                          setNewListWeekOfMonth(
                            Number(e.target.value) as 1 | 2 | 3 | 4 | 5
                          )
                        }
                      >
                        {WEEK_OF_MONTH_OPTIONS.map((week) => (
                          <MenuItem key={week.value} value={week.value}>
                            {week.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel>Day Of Week</InputLabel>
                      <Select
                        value={newListWeekday}
                        label="Day Of Week"
                        onChange={(e) =>
                          setNewListWeekday(Number(e.target.value))
                        }
                      >
                        {WEEKDAY_OPTIONS.map((day) => (
                          <MenuItem key={day.value} value={day.value}>
                            {day.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}

                {newListRecurrenceType === "annually" && (
                  <>
                    <FormControl fullWidth>
                      <InputLabel>Annual Cadence</InputLabel>
                      <Select
                        value={newListAnnualMode}
                        label="Annual Cadence"
                        onChange={(e) =>
                          setNewListAnnualMode(
                            e.target.value as
                              | "every_month"
                              | "every_other_month"
                              | "quarterly"
                              | "every_6_months"
                              | "specific_months"
                          )
                        }
                      >
                        {ANNUAL_MODE_OPTIONS.map((mode) => (
                          <MenuItem key={mode.value} value={mode.value}>
                            {mode.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {newListAnnualMode === "specific_months" && (
                      <FormControl fullWidth>
                        <InputLabel>Specific Months</InputLabel>
                        <Select
                          multiple
                          value={newListSpecificMonths}
                          label="Specific Months"
                          onChange={(e) =>
                            setNewListSpecificMonths(
                              (e.target.value as number[]).map(Number)
                            )
                          }
                        >
                          {MONTH_OPTIONS.map((month) => (
                            <MenuItem key={month.value} value={month.value}>
                              {month.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewListOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateList}
            disabled={!newListTitle.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <PageShareModal
        open={pageShareOpen}
        onClose={() => setPageShareOpen(false)}
        pageType="shopping"
      />
    </Box>
  );
};

export default ShoppingGroceries;
